import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  MdVerifiedUser, MdLocalHospital, MdScience,
  MdMedication, MdPeople, MdKingBed,
  MdFavorite, MdEmail, MdLock, MdVisibility, MdVisibilityOff,
  MdCheckCircle, MdWarning,
  MdSecurity, MdAccessTime, MdPersonOutline, MdKeyboardArrowRight,
  MdClose, MdDashboard, MdAnalytics,
  MdMedicalServices, MdCalendarToday, MdReceipt,
} from 'react-icons/md';

import { useAuth } from '../auth/AuthContext';
import { landingPathFor } from '../permissions';
import { PORTALS, DEFAULT_HOSPITAL_NAME } from '../config/systems';
import { isLiveApi, ensureApiReady, isApiHealthy, resetApiBase } from '../services/api';

/* ─── All 10 roles — complete, no missing ─── */
const ROLES = [
  { id: 'manager',   label: 'Manager',    sub: 'Full access',         icon: <MdDashboard />,     color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)',  email: 'manager@simha.hcen' },
  { id: 'doctor',    label: 'Doctor',     sub: 'OPD & EMR',           icon: <MdFavorite />,      color: '#0d9488', bg: 'rgba(13,148,136,0.1)',  email: 'doctor@simha.hcen' },
  { id: 'reception', label: 'Reception',  sub: 'OPD Queue',           icon: <MdPersonOutline />, color: '#3b82f6', bg: 'rgba(59,130,246,0.1)',  email: 'reception1@simha.hcen' },
  { id: 'nurse',     label: 'Nurse',      sub: 'IPD Station',         icon: <MdMedicalServices />,color: '#ec4899', bg: 'rgba(236,72,153,0.1)',  email: 'nurse@simha.hcen' },
  { id: 'ipd',       label: 'IP Coord',   sub: 'Beds & Admits',       icon: <MdKingBed />,       color: '#f97316', bg: 'rgba(249,115,22,0.1)',  email: 'ipd@simha.hcen' },
  { id: 'pharmacy',  label: 'Pharmacy',   sub: 'Dispensing',          icon: <MdMedication />,    color: '#10b981', bg: 'rgba(16,185,129,0.1)',  email: 'pharmacy@simha.hcen' },
  { id: 'lab',       label: 'Laboratory', sub: 'Tests & Results',     icon: <MdScience />,       color: '#06b6d4', bg: 'rgba(6,182,212,0.1)',   email: 'lab@simha.hcen' },
  { id: 'billing',   label: 'Billing',    sub: 'Payments & Reports',  icon: <MdReceipt />,       color: '#f59e0b', bg: 'rgba(245,158,11,0.1)',  email: 'billing@simha.hcen' },
  { id: 'hr',        label: 'HR',         sub: 'Staff & Payroll',     icon: <MdPeople />,        color: '#6366f1', bg: 'rgba(99,102,241,0.1)',  email: 'hr@simha.hcen' },
  { id: 'housekeeping', label: 'Housekeeping', sub: 'Cleaning & linen', icon: <MdLocalHospital />, color: '#14b8a6', bg: 'rgba(20,184,166,0.1)', email: 'housekeeping@simha.hcen' },
];

/* ─── Hero module showcase (like DocPulse top nav) ─── */
const MODULES = [
  { icon: <MdCalendarToday />, label: 'Appointments' },
  { icon: <MdPersonOutline />, label: 'Patients & EMR' },
  { icon: <MdKingBed />,       label: 'Inpatient (IPD)' },
  { icon: <MdMedication />,    label: 'Pharmacy' },
  { icon: <MdScience />,       label: 'Laboratory' },
  { icon: <MdReceipt />,       label: 'Billing' },
  { icon: <MdLocalHospital />, label: 'Nurse Station' },
  { icon: <MdAnalytics />,     label: 'Analytics' },
  { icon: <MdPeople />,        label: 'HR & Staff' },
];

/* ─── Hero stats ─── */
const STATS = [
  { val: '12+',    label: 'Modules' },
  { val: '99.9%',  label: 'Uptime' },
  { val: '<1s',    label: 'Response' },
  { val: 'AES-256',label: 'Encrypted' },
];

export default function LoginPage() {
  const { login }  = useAuth();
  const navigate   = useNavigate();

  /* form */
  const [email,      setEmail]      = useState(() => localStorage.getItem('hcen_email') || '');
  const [password,   setPassword]   = useState('');
  const [showPass,   setShowPass]   = useState(false);
  const [remember,   setRemember]   = useState(() => !!localStorage.getItem('hcen_email'));
  const [error,      setError]      = useState('');
  const [loading,    setLoading]    = useState(false);
  const [shaking,    setShaking]    = useState(false);
  const [success,    setSuccess]    = useState(false);

  /* ui */
  const [activeRole, setActiveRole] = useState(null);
  const [clock,      setClock]      = useState('');
  const [dateStr,    setDateStr]    = useState('');
  const [emailFocus, setEmailFocus] = useState(false);
  const [passFocus,  setPassFocus]  = useState(false);
  const [hoveredRole,setHoveredRole]= useState(null);
  const [connOk,       setConnOk]      = useState(null);

  const emailRef = useRef(null);
  const passRef  = useRef(null);

  /* live clock */
  useEffect(() => {
    const tick = () => {
      const n = new Date();
      setClock(n.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }));
      setDateStr(n.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }));
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  /* warm API connection on page load — only mark OK when /health returns ok:true */
  useEffect(() => {
    if (!isLiveApi()) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const ok = await isApiHealthy();
        if (!cancelled) setConnOk(ok);
      } catch {
        if (!cancelled) setConnOk(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* Role chip — selects role context only; never autofills demo passwords */
  const fillRole = (r) => {
    setActiveRole(r.id);
    setError('');
    emailRef.current?.focus();
  };

  /* submit — backend untouched */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmed = email.toLowerCase().trim();
    if (trimmed.endsWith('@networks.hcen.in')) {
      shake('HCEN company login uses Platform Admin — not this page.');
      return;
    }
    if (!trimmed || !password) {
      shake('Please enter your email and password.');
      return;
    }
    setLoading(true);
    try {
      if (remember) localStorage.setItem('hcen_email', trimmed);
      else          localStorage.removeItem('hcen_email');
      resetApiBase();
      const healthy = await isApiHealthy();
      if (!healthy) {
        setConnOk(false);
        throw new Error('API is down (HTTP 500). Upload C:\\Users\\gurus\\Desktop\\HCEN IP\\lambda\\dist\\HCEN_IP_API.zip to AWS Lambda HCEN_IP_API, wait 30s, then refresh and try again.');
      }
      setConnOk(true);
      await ensureApiReady();
      const session = await login(trimmed, password);
      setSuccess(true);
      setTimeout(() => navigate(landingPathFor(session.role), { replace: true }), 700);
    } catch (err) {
      const msg = err.message || 'Invalid email or password. Please try again.';
      if (/cannot reach server|failed to fetch|timed out|slow/i.test(msg)) {
        resetApiBase();
        setConnOk(false);
      }
      shake(msg);
    } finally {
      setLoading(false);
    }
  };

  const shake = (msg) => {
    setError(msg);
    setShaking(true);
    setTimeout(() => setShaking(false), 650);
  };

  const apiLive = isLiveApi();

  return (
    <div className="hv3-page">

      {/* ═══════════════ HERO LEFT ═══════════════ */}
      <aside className="hv3-hero">
        {/* Background layers */}
        <div className="hv3-hero-bg" />
        <div className="hv3-hero-dots" />
        <div className="hv3-blob hv3-b1" />
        <div className="hv3-blob hv3-b2" />
        <div className="hv3-blob hv3-b3" />

        {/* Top: brand + clock */}
        <div className="hv3-hero-top">
          <div className="hv3-brand">
            <div className="hv3-brand-logo">
              <img src="/hcen_logo.png" alt="HCEN" />
            </div>
            <div className="hv3-brand-text">
              <div className="hv3-brand-name">HCEN HMS</div>
              <div className="hv3-brand-tag">Hospital Management System</div>
            </div>
          </div>
          <div className="hv3-clock-box">
            <MdAccessTime style={{ opacity: 0.8, fontSize: 14 }} />
            <span className="hv3-clock-time">{clock}</span>
          </div>
        </div>

        {/* Hospital badge */}
        <div className="hv3-hospital-badge">
          <MdLocalHospital style={{ fontSize: 16 }} />
          <span>{DEFAULT_HOSPITAL_NAME}</span>
          <span className="hv3-hosp-dot" />
          <span style={{ fontSize: 11, opacity: 0.8 }}>Powered by HCEN</span>
        </div>

        {/* Headline */}
        <div className="hv3-headline">
          <h2 className="hv3-h2">
            One system.<br />
            Every department.<br />
            Zero confusion.
          </h2>
          <p className="hv3-desc">
            OPD · EMR · IPD · Pharmacy · Lab · Billing · HR<br />
            Connected, fast, and built for real hospitals.
          </p>
        </div>

        {/* Module grid (like DocPulse top nav, but cards) */}
        <div className="hv3-modules-label">Active modules</div>
        <div className="hv3-modules-grid">
          {MODULES.map((m, i) => (
            <div key={i} className="hv3-module-card" style={{ animationDelay: `${i * 0.05}s` }}>
              <span className="hv3-module-ico">{m.icon}</span>
              <span className="hv3-module-label">{m.label}</span>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div className="hv3-stats">
          {STATS.map((s, i) => (
            <div key={i} className="hv3-stat">
              <div className="hv3-stat-val">{s.val}</div>
              <div className="hv3-stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Date footer */}
        <div className="hv3-hero-date">{dateStr}</div>
      </aside>

      {/* ═══════════════ RIGHT PANEL ═══════════════ */}
      <div className="hv3-panel">

        {/* Offline banner */}
        {!apiLive && (
          <div className="hv3-offline-banner">
            <MdWarning />
            <span>API not connected — rebuild with <code>REACT_APP_IP_API_URL</code> and deploy to S3.</span>
          </div>
        )}

        {/* ── Login Card ── */}
        <div className={`hv3-card${shaking ? ' hv3-shake' : ''}${success ? ' hv3-done' : ''}`}>

          {/* Card top accent bar */}
          <div className="hv3-card-accent" />

          {/* Card header */}
          <div className="hv3-card-head">
            <img src="/hcen_logo.png" alt="HCEN" className="hv3-card-logo" />
            <div className="hv3-card-head-text">
              <h1 className="hv3-card-title">Welcome back</h1>
              <p className="hv3-card-sub">
                Sign in to <strong>{PORTALS.NETWORKS.domain}</strong>
              </p>
            </div>
            {/* Live status dot */}
            <div
              className={`hv3-live-dot ${apiLive ? 'online' : 'offline'}`}
              title={apiLive ? 'System online' : 'System offline'}
            >
              <span className="hv3-dot-pulse" />
              <span className="hv3-dot-core" />
            </div>
          </div>

          {/* ── Role selector ── */}
          <div className="hv3-role-wrap">
            <div className="hv3-role-hdr">
              <span className="hv3-role-hdr-label">Select your role (optional)</span>
            </div>

            {/* 3×3 role grid */}
            <div className="hv3-role-grid">
              {ROLES.map(r => {
                const isActive  = activeRole === r.id;
                const isHovered = hoveredRole === r.id;
                return (
                  <button
                    key={r.id}
                    type="button"
                    className={`hv3-role-chip${isActive ? ' active' : ''}`}
                    style={{
                      borderColor: (isActive || isHovered) ? r.color : undefined,
                      backgroundColor: isActive ? r.bg : (isHovered ? r.bg : undefined),
                      color: (isActive || isHovered) ? r.color : undefined,
                    }}
                    onClick={() => fillRole(r)}
                    onMouseEnter={() => setHoveredRole(r.id)}
                    onMouseLeave={() => setHoveredRole(null)}
                    title={`Continue as ${r.label} · ${r.sub}`}
                  >
                    <span
                      className="hv3-chip-ico"
                      style={{ color: (isActive || isHovered) ? r.color : '#94a3b8' }}
                    >
                      {r.icon}
                    </span>
                    <div className="hv3-chip-text">
                      <span className="hv3-chip-label">{r.label}</span>
                      <span className="hv3-chip-sub">{r.sub}</span>
                    </div>
                    {isActive && <MdCheckCircle className="hv3-chip-check" style={{ color: r.color }} />}
                  </button>
                );
              })}
            </div>
            {activeRole && (
              <p className="hint" style={{ marginTop: 10, marginBottom: 0 }}>
                Signing in as <strong>{ROLES.find((r) => r.id === activeRole)?.label}</strong> — enter your hospital staff email and password.
              </p>
            )}
          </div>

          {/* ── Form ── */}
          <form onSubmit={handleSubmit} className="hv3-form" noValidate>

            {/* Email */}
            <div className={`hv3-field${emailFocus ? ' hv3-focused' : ''}${email ? ' hv3-filled' : ''}`}>
              <label htmlFor="hv3-email" className="hv3-label">Staff email</label>
              <div className="hv3-input-row">
                <MdEmail className="hv3-field-ico" />
                <input
                  id="hv3-email"
                  ref={emailRef}
                  type="email"
                  className="hv3-input"
                  value={email}
                  placeholder="your.name@hospital.hcen"
                  autoComplete="username"
                  required
                  onChange={e => { setEmail(e.target.value); setError(''); setActiveRole(null); }}
                  onFocus={() => setEmailFocus(true)}
                  onBlur={() => setEmailFocus(false)}
                />
                {email && (
                  <button
                    type="button"
                    className="hv3-input-action"
                    onClick={() => { setEmail(''); setActiveRole(null); emailRef.current?.focus(); }}
                    tabIndex={-1}
                  >
                    <MdClose size={15} />
                  </button>
                )}
              </div>
            </div>

            {/* Password */}
            <div className={`hv3-field${passFocus ? ' hv3-focused' : ''}${password ? ' hv3-filled' : ''}`}>
              <div className="hv3-label-row">
                <label htmlFor="hv3-pass" className="hv3-label">Password</label>
                <button
                  type="button"
                  className="hv3-forgot-btn"
                  tabIndex={-1}
                  title="Contact your hospital administrator to reset your password"
                >
                  Forgot password?
                </button>
              </div>
              <div className="hv3-input-row">
                <MdLock className="hv3-field-ico" />
                <input
                  id="hv3-pass"
                  ref={passRef}
                  type={showPass ? 'text' : 'password'}
                  className="hv3-input"
                  value={password}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  onChange={e => { setPassword(e.target.value); setError(''); }}
                  onFocus={() => setPassFocus(true)}
                  onBlur={() => setPassFocus(false)}
                />
                <button
                  type="button"
                  className="hv3-input-action"
                  onClick={() => setShowPass(v => !v)}
                  tabIndex={-1}
                  title={showPass ? 'Hide password' : 'Show password'}
                >
                  {showPass ? <MdVisibilityOff size={17} /> : <MdVisibility size={17} />}
                </button>
              </div>
            </div>

            {/* Remember me */}
            <label className="hv3-remember">
              <span className="hv3-check-wrap">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={e => setRemember(e.target.checked)}
                  className="hv3-check-input"
                />
                <span className="hv3-check-box" />
              </span>
              <span>Remember my email on this device</span>
            </label>

            {/* Connection status */}
            {apiLive && connOk === false && !error && (
              <div className="hv3-error" role="status">
                <MdWarning className="hv3-alert-ico" />
                <span>
                  API is down (HTTP 500). Upload{' '}
                  <code>C:\Users\gurus\Desktop\HCEN IP\lambda\dist\HCEN_IP_API.zip</code>
                  {' '}to AWS Lambda <strong>HCEN_IP_API</strong>, wait 30s, then refresh this page.
                </span>
              </div>
            )}
            {apiLive && connOk === true && !error && !success && (
              <div className="hv3-success" role="status" style={{ marginBottom: 0 }}>
                <MdCheckCircle className="hv3-alert-ico" />
                <span>API connected — ready to sign in</span>
              </div>
            )}

            {/* Error */}
            {error && !success && (
              <div className="hv3-error" role="alert">
                <MdWarning className="hv3-alert-ico" />
                <span>{error}</span>
              </div>
            )}

            {/* Success */}
            {success && (
              <div className="hv3-success" role="status">
                <MdCheckCircle className="hv3-alert-ico" />
                <span>Signed in successfully! Redirecting…</span>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              id="hv3-submit"
              className={`hv3-submit${loading ? ' hv3-loading' : ''}${success ? ' hv3-done-btn' : ''}`}
              disabled={loading || !apiLive || success}
            >
              {loading ? (
                <><span className="hv3-spinner" /> Signing in…</>
              ) : success ? (
                <><MdCheckCircle size={18} /> Signed in</>
              ) : (
                <>Sign in <MdKeyboardArrowRight className="hv3-arrow" /></>
              )}
            </button>

            {/* Security line */}
            <div className="hv3-security">
              <MdSecurity className="hv3-sec-ico" />
              <span>AWS Cognito · AES-256 encryption · Role-based access control</span>
            </div>
          </form>

          {/* Footer */}
          <div className="hv3-card-foot">
            <span>HCEN company admin?</span>
            <Link to="/platform/login" className="hv3-platform-link">
              <MdVerifiedUser size={14} />
              Platform Admin login
              <MdKeyboardArrowRight size={14} />
            </Link>
          </div>
        </div>

        {/* Panel footer */}
        <div className="hv3-panel-foot">
          © 2026 HCEN Networks · <a href="https://hcen.in" target="_blank" rel="noreferrer">hcen.in</a> · All rights reserved
        </div>
      </div>
    </div>
  );
}
